import Accordion from '@/common/components/Accordion'
import MaintenanceObjective from '@/common/components/MaintenanceObjective'
import Button from '@/common/components/Button'
import { ImageCarousel } from '@/common/components/ImageCarousel'
import { ImageWithPlaceholder } from '@/common/components/ImageWithPlaceholder'
import ResumeItem from '@/common/components/ResumeItem'
import { cn } from '@/common/utils/cn'
import ExecutionEntryObjectiveActivities from '@/features/maintenanceWorkflow/pages/Show/features/directiveEntries/features/execution/components/ExecutionEntryObjectiveActivities'
import ExecutionEntryObjectivePieces from '@/features/maintenanceWorkflow/pages/Show/features/directiveEntries/features/execution/components/ExecutionEntryObjectivePieces'
import { MaintenanceExecutionObjectiveApprove } from '@/features/maintenanceWorkflow/pages/Show/features/directiveEntries/features/execution/components/MaintenanceExecutionObjectiveApprove'
import { MaintenanceExecutionObjectiveDisapprove } from '@/features/maintenanceWorkflow/pages/Show/features/directiveEntries/features/execution/components/MaintenanceExecutionObjectiveDisapprove'
import { MaintenanceDirectiveObjectiveData } from '@/features/maintenanceWorkflow/pages/Show/features/forwardings/types/maintenanceForwarding.type'
import { CameraIcon } from 'lucide-react'
import { useEffect, useState } from 'react'

interface ExecutionObjectiveItemProps {
  objective: MaintenanceDirectiveObjectiveData
  isInAwaitingClientReview: boolean
}

const ExecutionObjectiveItem = ({ objective, isInAwaitingClientReview }: ExecutionObjectiveItemProps) => {
  const [isOpen, setIsOpen] = useState(true)

  const [reviewState, setReviewState] = useState(objective.last_review?.state)

  const isApproved = reviewState === 'approved'
  const isDisapproved = reviewState === 'disapproved'

  useEffect(() => {
    setReviewState(objective.last_review?.state)
  }, [objective.last_review?.state])

  return (
    <Accordion
      defaultOpen
      open={isOpen}
      onToggle={setIsOpen}
      className={cn(
        'p-0 rounded-lg',
        isApproved && isInAwaitingClientReview && 'bg-success-op',
        isDisapproved && isInAwaitingClientReview && 'bg-yellow-op-2',
      )}
      triggerClassName="p-0 pr-4 bg-white-100 rounded-t-lg"
      bodyClassName="p-4"
      head={
        <div className="flex items-center flex-wrap gap-4 w-full pr-4">
          <div className="flex items-start flex-wrap gap-4">
            <div onClick={(e) => e.stopPropagation()}>
              <ImageCarousel
                files={objective.photos?.map((photo) => ({
                  id: photo.id,
                  file: photo.url,
                }))}
                Placeholder={
                  <ImageWithPlaceholder
                    className={cn('size-28 rounded-md', isDisapproved && 'size-40')}
                    iconClassName="size-14"
                    placeholderIcon={CameraIcon}
                  />
                }
                className={cn('size-28', isDisapproved && 'size-40')}
                showExpandButton
                showRemoveButton={false}
              />
            </div>

            <div className="flex flex-col items-start gap-2 py-3">
              <MaintenanceObjective
                systemDisplayName={objective.system.display_name}
                actionDisplayName={objective.action.display_name}
                partDisplayName={objective.part.display_name}
                actionDescription={objective.action.description}
              />

              {isDisapproved && (
                <ResumeItem
                  containerClassName="items-start"
                  className="text-xs"
                  label="Justificativa"
                  value={objective?.last_review?.description || '--'}
                />
              )}
            </div>
          </div>

          <div className="ml-auto flex items-center gap-3" onClick={(e) => e.stopPropagation()}>
            {isInAwaitingClientReview && (
              <>
                {isDisapproved ? (
                  <Button variant="yellow" className="pointer-events-none">
                    Declinado
                  </Button>
                ) : (
                  <MaintenanceExecutionObjectiveDisapprove
                    objectiveId={objective.id}
                    onDisapprove={() => {
                      setReviewState('disapproved')
                      setIsOpen(false)
                    }}
                  />
                )}

                {isApproved ? (
                  <Button variant="neutral" className="pointer-events-none">
                    Aprovado
                  </Button>
                ) : (
                  <MaintenanceExecutionObjectiveApprove
                    objectiveId={objective.id}
                    onApprove={() => {
                      setReviewState('approved')
                      setIsOpen(false)
                    }}
                  />
                )}
              </>
            )}
          </div>
        </div>
      }
      body={
        <div className="flex flex-col gap-3">
          <ExecutionEntryObjectivePieces objective={objective} />

          <ExecutionEntryObjectiveActivities objective={objective} />
        </div>
      }
    />
  )
}

export default ExecutionObjectiveItem
